// File: frontend/src/components/admin/AdminCustomTopics.tsx

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaSearch,
  FaTimes,
  FaCheck,
  FaBan,
  FaHashtag,
  FaPlus,
  FaEdit,
  FaLock,
  FaUnlock,
  FaInfoCircle,
  FaToggleOn,
  FaToggleOff,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaFilter,
  FaChevronDown,
  FaArrowUp,
  FaArrowDown,
  FaTrash,
  FaExclamationTriangle,
} from "react-icons/fa";

import {
  CustomTopicSuggestionDto,
  SuggestionStatus,
  getSuggestions,
  getPendingSuggestionCount,
  approveSuggestion,
  rejectSuggestion,
  getAdminTopics,
  createTopic,
  updateTopic,
  deleteTopic,
} from "../../services/adminCustomTopicService";
import { CustomTopicModel } from "../../models/CustomTopicModel";
import { customTopicService } from "../../services/customTopicService";
import { getPublicTopicCategories, adminCreateTopicCategory, MdTopicCategoryDto } from "../../services/masterDataService";
import { Pagination } from "../../utils/Pagination";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";
import { AdminSuccessBanner, AdminErrorBanner } from "../common/AdminBanner";
import { ConfirmModal } from "../common/ConfirmModal";
import { LockReasonFields } from "./common/LockReasonFields";
import { LockInfoModal } from "./common/LockInfoModal";
import {
  ADMIN_PAGE_SIZE,
  ADMIN_ADMIN_NOTE_MAX_LENGTH,
  ADMIN_TOPIC_SLUG_MAX_LENGTH,
  ADMIN_MD_CATEGORY_NAME_MAX_LENGTH,
} from "../../constants";
// Shared "current / max" counter shown under every free-text modal field
import { AdminCharCounter, AdminRefreshButton } from "./common";
import { formatAdminDate, formatDaysDiff, formatRefreshTime } from "../../utils/adminUtils";
import { parseApiError } from "../../utils/parseApiError";
import { normalizeSearchText } from "../../utils/searchUtils";
import { formatCompactWithTitle } from "../../utils/numberFormatUtils";
import { InputClearButton } from "../common/InputClearButton";
import "./AdminCustomTopics.css";

interface AdminCustomTopicsProps {
  isAdmin: boolean;
}

// Inner tabs: pending suggestions review vs. published topics management
type InnerTab = "suggestions" | "topics";

// Max title length — mirrors the backend CustomTopic.title column limit
const MAX_TITLE_LENGTH = 80;
// Max channels ("kanal") per topic — mirrors backend CustomTopicService.MAX_CHANNELS_PER_TOPIC
const MAX_CHANNELS_PER_TOPIC = 10;
// Column widths of the Başlıklar table that had to change when the hard-delete
// button was added — see the TOPICS_COLUMN_WIDTHS comment in the header row.
// Mirrors .ct-topics-table .ct-col-* in AdminCustomTopics.css; the full set of
// topic column widths still totals exactly 100%.
const TOPICS_COLUMN_WIDTHS = {
  // Başlık stays auto on purpose — under table-layout: fixed the auto column
  // absorbs whatever width the other columns do not use
  title:   "auto",
  slug:    "15%",
  // Exactly the width of four action buttons plus gaps and cell padding
  actions: "128px",
} as const;

// Typeahead fires from the 3rd character, debounced
const TYPEAHEAD_MIN_CHARS = 3;
const TYPEAHEAD_DEBOUNCE_MS = 300;

// Normal channels are listed alphabetically everywhere in this panel (picker and
// filter selects) — Turkish collation so ç/ğ/ı/ö/ş/ü sort in their proper
// place instead of after "z"
// Special channels (İstikram / Bırak system channels and the fixed "nickaltı"
// channel) always go to the bottom, alphabetical among themselves.
const NICKALTI_CHANNEL_NAME = "nickaltı"; // mirrors backend NickaltiTopicService.NICKALTI_CHANNEL_NAME
const isSpecialChannel = (c: MdTopicCategoryDto): boolean =>
  !!c.systemKey || c.name === NICKALTI_CHANNEL_NAME;
const sortChannelsByName = (list: MdTopicCategoryDto[]): MdTopicCategoryDto[] =>
  [...list].sort((a, b) => {
    const specialDiff = Number(isSpecialChannel(a)) - Number(isSpecialChannel(b));
    return specialDiff !== 0 ? specialDiff : a.name.localeCompare(b.name, "tr");
  });

// ── Channel ("kanal") picker — approve/create/edit modals ─────────────────
// Ordered multi selection with hashtag semantics: up to 10 channels per topic.
// Index 0 is the PRIMARY channel (its icon shows next to the title in the
// public list), so admins/mods can reorder the chips with the ↑/↓ buttons —
// unlike the public suggest modal, where plain selection order is enough.
// The approve modal additionally shows the user-proposed NEW channel names
// (dashed chips) — kept names are auto-created by the backend on approval.
// Admins can also create a channel inline ("yeni kanal") without leaving the
// modal; it lands in master data at its alphabetical position and is selected
// immediately.
// Channels are added via a searchable combobox (same UX as the public suggest
// modal): typing filters the alphabetical list, each option shows its active
// topic count in compact form ("1,2k").
interface ChannelPickerProps {
  value: number[];                    // Ordered selected channel ids
  onChange: (ids: number[]) => void;
  categories: MdTopicCategoryDto[];   // All active channels for the add-select
  // User-proposed NEW channel names (approve modal only) — dashed chips,
  // removable; the backend creates the kept ones on approval
  proposedNames?: string[];
  onProposedNamesChange?: (names: string[]) => void;
  // Shows the inline "yeni kanal" creator — the create endpoint is admin-only
  canCreateChannel?: boolean;
  // Called after an inline create succeeds so the parent can refresh its
  // category list (the new channel is auto-selected here)
  onCategoryCreated?: (created: MdTopicCategoryDto) => void;
  // Active topic count per channel id — shown next to each dropdown option
  // (channels missing from the map have no active topic → 0)
  topicCounts?: Record<number, number>;
}

const ChannelPicker: React.FC<ChannelPickerProps> = ({
  value, onChange, categories, proposedNames, onProposedNamesChange, canCreateChannel, onCategoryCreated, topicCounts,
}) => {
  const { t } = useTranslation();

  // Searchable channel combobox: free-text query + dropdown visibility
  const [channelQuery, setChannelQuery]               = useState("");
  const [channelDropdownOpen, setChannelDropdownOpen] = useState(false);
  // Closes the channel dropdown on outside clicks
  const channelBoxRef = useRef<HTMLDivElement | null>(null);

  // Close the dropdown when clicking anywhere outside the combobox.
  // Listens on "click" (NOT "mousedown") on purpose — same reason as in the
  // public suggest modal: the dropdown is rendered in normal flow and pushes
  // the modal footer down; closing it on mousedown would move the footer
  // buttons out from under the pointer before mouseup and swallow the click.
  useEffect(() => {
    const onDocumentClick = (e: MouseEvent) => {
      if (channelBoxRef.current && !channelBoxRef.current.contains(e.target as Node)) {
        setChannelDropdownOpen(false);
      }
    };
    document.addEventListener("click", onDocumentClick);
    return () => document.removeEventListener("click", onDocumentClick);
  }, []);

  // Inline "yeni kanal" creator state
  const [creatorOpen, setCreatorOpen]   = useState(false);
  const [creatorName, setCreatorName]   = useState("");
  const [creatorBusy, setCreatorBusy]   = useState(false);
  const [creatorError, setCreatorError] = useState<string | null>(null);

  // Existing + proposed chips share the single 10-channel cap
  const proposed   = proposedNames ?? [];
  const totalCount = value.length + proposed.length;

  const addChannel = (id: number) => {
    if (value.includes(id) || totalCount >= MAX_CHANNELS_PER_TOPIC) return;
    onChange([...value, id]);
    setChannelQuery("");
  };

  // Channel names are lowercase-only (sözlük convention) — Turkish locale
  // so dotted/dotless i is handled correctly
  const normalizedChannelQuery = channelQuery.trim().toLocaleLowerCase("tr");

  // Dropdown list: channels matching the query, minus already-picked ones.
  // The parent keeps `categories` sorted alphabetically; sorting again here
  // keeps the picker correct even if a caller passes an unsorted list.
  // Diacritic-blind matching (same as the public suggest modal): typing
  // "cografya" / "dunya" finds "coğrafya" / "dünya" — normalizeSearchText folds
  // ç ğ ı İ ö ş ü (and â î û etc.) on BOTH the query and the channel name
  const foldedChannelQuery = normalizeSearchText(normalizedChannelQuery);
  const filteredCategories = sortChannelsByName(categories).filter(
    (c) => !value.includes(c.id) &&
      (!foldedChannelQuery || normalizeSearchText(c.name).includes(foldedChannelQuery))
  );

  const removeChannel = (id: number) => {
    onChange(value.filter((c) => c !== id));
  };

  const removeProposedName = (name: string) => {
    onProposedNamesChange?.(proposed.filter((n) => n !== name));
  };

  // Create a channel inline via the admin master data endpoint — the backend
  // slots it into its alphabetical display position. On success the channel
  // is appended to the selection and the parent refreshes its category list.
  const handleInlineCreate = async () => {
    // Channel names are lowercase-only (sözlük convention)
    const name = creatorName.trim().toLocaleLowerCase("tr");
    if (!name || creatorBusy) return;
    setCreatorBusy(true);
    setCreatorError(null);
    try {
      const created = await adminCreateTopicCategory({ name, iconKey: "" });
      onCategoryCreated?.(created);
      if (!value.includes(created.id) && totalCount < MAX_CHANNELS_PER_TOPIC) {
        onChange([...value, created.id]);
      }
      setCreatorName("");
      setCreatorOpen(false);
    } catch {
      // Most likely a 409 — a channel with this name already exists
      setCreatorError(t("topic_channel_create_error", "Kanal oluşturulamadı — aynı adda bir kanal zaten olabilir."));
    } finally {
      setCreatorBusy(false);
    }
  };

  // Swap the chip with its neighbor — keeps the list contiguous
  const moveChannel = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= value.length) return;
    const next = [...value];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <div className="ct-channel-picker">
      {value.length > 0 && (
        <div className="ct-channel-chips">
          {value.map((id, index) => {
            const cat = categories.find((c) => c.id === id);
            // Channels not in the active list (e.g. deactivated after selection)
            // still render by id so they remain removable
            const name = cat ? cat.name : `#${id}`;
            return (
              <span key={id} className="ct-channel-chip">
                {name}
                <span className="ct-channel-chip-actions">
                  <button
                    type="button"
                    className="ct-channel-chip-btn"
                    onClick={() => moveChannel(index, -1)}
                    disabled={index === 0}
                    title={t("topic_channel_move_up", "Yukarı taşı")}
                  >
                    <FaArrowUp size={8} />
                  </button>
                  <button
                    type="button"
                    className="ct-channel-chip-btn"
                    onClick={() => moveChannel(index, 1)}
                    disabled={index === value.length - 1}
                    title={t("topic_channel_move_down", "Aşağı taşı")}
                  >
                    <FaArrowDown size={8} />
                  </button>
                  <button
                    type="button"
                    className="ct-channel-chip-btn ct-channel-chip-remove"
                    onClick={() => removeChannel(id)}
                    title={t("topic_channel_remove", "Kaldır")}
                  >
                    <FaTimes size={9} />
                  </button>
                </span>
              </span>
            );
          })}
        </div>
      )}
      {/* User-proposed NEW channels — dashed chips; kept ones are created by
          the backend (alphabetical master data position) on approval */}
      {proposed.length > 0 && (
        <div className="ct-channel-chips">
          {proposed.map((name) => (
            <span key={name} className="ct-channel-chip ct-channel-chip--new">
              {name}
              <span className="ct-channel-new-badge">{t("topic_channel_new_badge", "yeni")}</span>
              <span className="ct-channel-chip-actions">
                <button
                  type="button"
                  className="ct-channel-chip-btn ct-channel-chip-remove"
                  onClick={() => removeProposedName(name)}
                  title={t("topic_channel_remove", "Kaldır")}
                >
                  <FaTimes size={9} />
                </button>
              </span>
            </span>
          ))}
        </div>
      )}
      {totalCount < MAX_CHANNELS_PER_TOPIC ? (
        /* The wrapper carries the outside-click ref so clicks on the dropdown
           itself never count as "outside" */
        <div className="ct-channel-combobox" ref={channelBoxRef}>
          <div className="ct-channel-search">
            <FaSearch size={10} className="ct-channel-search-icon" />
            <input
              type="text"
              value={channelQuery}
              // Channel names are lowercase-only (sözlük convention)
              onChange={(e) => {
                setChannelQuery(e.target.value.toLocaleLowerCase("tr"));
                setChannelDropdownOpen(true);
              }}
              onFocus={() => setChannelDropdownOpen(true)}
              onKeyDown={(e) => {
                // Enter picks the first match — and must never submit the surrounding modal
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                  if (normalizedChannelQuery && filteredCategories.length > 0) {
                    addChannel(filteredCategories[0].id);
                  }
                }
              }}
              placeholder={t("topic_channel_add_placeholder", "kanal ekle...")}
              maxLength={ADMIN_MD_CATEGORY_NAME_MAX_LENGTH}
            />
            {channelQuery !== "" && (
              <InputClearButton onClear={() => setChannelQuery("")} />
            )}
          </div>
          {/* Rendered in normal flow (NOT absolute) so it can never be clipped
              by the modal edge — it pushes the modal body down instead */}
          {channelDropdownOpen && (
            <div className="ct-channel-dropdown">
              {filteredCategories.map((c) => {
                // Compact topic count ("1,2k"), exact value as tooltip when shortened
                const count = formatCompactWithTitle(topicCounts?.[c.id] ?? 0);
                return (
                  <button
                    key={c.id}
                    type="button"
                    className="ct-channel-option"
                    onClick={() => addChannel(c.id)}
                  >
                    <span className="ct-channel-option-name">#{c.name}</span>
                    <span className="ct-channel-option-count" title={count.title}>
                      {count.text}
                    </span>
                  </button>
                );
              })}
              {filteredCategories.length === 0 && (
                <div className="ct-channel-empty">
                  {t("topic_channel_no_results", "kanal bulunamadı")}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="ct-channel-max">{t("topic_channel_max_reached", { max: MAX_CHANNELS_PER_TOPIC })}</div>
      )}
      {/* Inline "yeni kanal" creator — admin-only (the master data create
          endpoint requires ADMIN). Created channels are slotted into their
          alphabetical position by the backend and selected right away. */}
      {canCreateChannel && totalCount < MAX_CHANNELS_PER_TOPIC && (
        creatorOpen ? (
          <div className="ct-channel-creator">
            <input
              type="text"
              value={creatorName}
              // Channel names are lowercase-only (sözlük convention)
              onChange={(e) => { setCreatorName(e.target.value.toLocaleLowerCase("tr")); setCreatorError(null); }}
              onKeyDown={(e) => {
                // Enter creates; must never submit the surrounding modal
                if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); handleInlineCreate(); }
              }}
              placeholder={t("topic_channel_create_placeholder", "yeni kanal adı")}
              maxLength={ADMIN_MD_CATEGORY_NAME_MAX_LENGTH}
              autoFocus
            />
            <AdminCharCounter value={creatorName} max={ADMIN_MD_CATEGORY_NAME_MAX_LENGTH} />
            <button
              type="button"
              className="ct-channel-creator-save"
              onClick={handleInlineCreate}
              disabled={creatorBusy || !creatorName.trim()}
            >
              {t("topic_channel_create_btn", "Oluştur")}
            </button>
            <button
              type="button"
              className="ct-channel-creator-cancel"
              onClick={() => { setCreatorOpen(false); setCreatorName(""); setCreatorError(null); }}
            >
              <FaTimes size={10} />
            </button>
          </div>
        ) : (
          <button type="button" className="ct-channel-creator-toggle" onClick={() => setCreatorOpen(true)}>
            <FaPlus size={9} /> {t("topic_channel_create_new", "yeni kanal")}
          </button>
        )
      )}
      {canCreateChannel && creatorError && (
        <div className="ct-channel-creator-error">{creatorError}</div>
      )}
      {/* The first chip drives the icon shown next to the topic in the public list */}
      <div className="ct-channel-primary-hint">
        {t("topic_channel_primary_hint", "İlk kanal, başlığın listede görünen ikonunu belirler.")}
      </div>
    </div>
  );
};

// ── Topics table column sorting ─────────────────────────────────
// The topics table is server-paginated (unlike the fully-loaded rank boards),
// so sorting re-fetches from the backend instead of sorting in memory. The
// click/icon behavior still mirrors AdminRankBoard: first click uses the
// natural direction for that column's type, a second click flips it.
type TopicSortField = "id" | "title" | "entryCount" | "suggestedByUsername" | "createdByUsername" | "suggestedAt" | "daysDiff" | "createdAt";
type TopicSortColumnType = "number" | "string" | "date";
type TopicSortDirection = "asc" | "desc";

const TOPIC_SORT_COLUMN_TYPES: Record<TopicSortField, TopicSortColumnType> = {
  id: "number",
  title: "string",
  entryCount: "number",
  suggestedByUsername: "string",
  createdByUsername: "string",
  suggestedAt: "date",
  daysDiff: "number",
  createdAt: "date",
};

// First-click direction per column type — numbers highest first (e.g. most
// entries), dates oldest first, text A→Z. Same convention as AdminRankBoard.
const defaultTopicSortDirection = (type: TopicSortColumnType): TopicSortDirection =>
  type === "number" ? "desc" : "asc";

// ── Suggestions table column sorting — same mechanism as the topics table
// above (reuses TopicSortColumnType/TopicSortDirection/defaultTopicSortDirection) ──
type SuggestionSortField = "id" | "title" | "suggestedByUsername" | "reviewedAt" | "daysDiff" | "createdAt";

const SUGGESTION_SORT_COLUMN_TYPES: Record<SuggestionSortField, TopicSortColumnType> = {
  id: "number",
  title: "string",
  suggestedByUsername: "string",
  reviewedAt: "date",
  daysDiff: "number",
  createdAt: "date",
};

export const AdminCustomTopics: React.FC<AdminCustomTopicsProps> = ({ isAdmin }) => {
  const { t } = useTranslation();

  // ── Inner tab state ───────────────────────────────────────────
  const [innerTab, setInnerTab] = useState<InnerTab>("suggestions");
  const [pendingCount, setPendingCount] = useState(0);

  // ── Banner state ──────────────────────────────────────────────
  const [successMessage, setSuccessMessage, clearSuccess] = useAutoDismiss();
  const [error, setError] = useState<string | null>(null);

  // ── Suggestions state ─────────────────────────────────────────
  const [suggestions, setSuggestions] = useState<CustomTopicSuggestionDto[]>([]);
  const [suggestionStatus, setSuggestionStatus] = useState<SuggestionStatus | "">("PENDING");
  const [suggestionCategoryFilter, setSuggestionCategoryFilter] = useState<number | "">("");
  const [suggestionSuggestedByFilter, setSuggestionSuggestedByFilter] = useState("");
  const [suggestionDateFrom, setSuggestionDateFrom] = useState("");
  const [suggestionDateTo, setSuggestionDateTo] = useState("");
  const [suggestionReviewedDateFrom, setSuggestionReviewedDateFrom] = useState("");
  const [suggestionReviewedDateTo, setSuggestionReviewedDateTo] = useState("");
  const [showSuggestionFilters, setShowSuggestionFilters] = useState(true);
  const [suggestionsLoading, setSuggestionsLoading] = useState(true);
  const [suggestionsRefreshing, setSuggestionsRefreshing] = useState(false);
  const [suggestionsLastRefreshedAt, setSuggestionsLastRefreshedAt] = useState<Date | null>(null);
  const [suggestionsPage, setSuggestionsPage] = useState(0);
  const [suggestionSortField, setSuggestionSortField] = useState<SuggestionSortField>("createdAt");
  const [suggestionSortDir, setSuggestionSortDir] = useState<TopicSortDirection>("desc");
  const [suggestionsTotalPages, setSuggestionsTotalPages] = useState(0);
  const [suggestionsTotalItems, setSuggestionsTotalItems] = useState(0);

  // ── Topics state ──────────────────────────────────────────────
  const [topics, setTopics] = useState<CustomTopicModel[]>([]);
  const [topicsLoading, setTopicsLoading] = useState(true);
  const [topicsRefreshing, setTopicsRefreshing] = useState(false);
  const [topicsLastRefreshedAt, setTopicsLastRefreshedAt] = useState<Date | null>(null);
  const [topicsPage, setTopicsPage] = useState(0);
  const [topicsTotalPages, setTopicsTotalPages] = useState(0);
  const [topicsTotalItems, setTopicsTotalItems] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [topicActiveFilter, setTopicActiveFilter] = useState<"" | "true" | "false">("");
  const [topicCategoryFilter, setTopicCategoryFilter] = useState<number | "">("");
  const [topicSuggestedByFilter, setTopicSuggestedByFilter] = useState("");
  const [topicCreatedByFilter, setTopicCreatedByFilter] = useState("");
  const [topicDateFrom, setTopicDateFrom] = useState("");
  const [topicDateTo, setTopicDateTo] = useState("");
  const [topicSuggestedDateFrom, setTopicSuggestedDateFrom] = useState("");
  const [topicSuggestedDateTo, setTopicSuggestedDateTo] = useState("");
  const [topicLockedFilter, setTopicLockedFilter] = useState<"" | "true" | "false">("");
  const [showTopicFilters, setShowTopicFilters] = useState(true);
  // Column sort — defaults to createdAt/desc, i.e. exactly the previous
  // (non-sortable) behavior, so nothing changes until an admin clicks a header.
  const [topicSortField, setTopicSortField] = useState<TopicSortField>("createdAt");
  const [topicSortDir, setTopicSortDir] = useState<TopicSortDirection>("desc");

  // ── Lock modal state (shown only when locking, not unlocking) ──
  const [lockTarget, setLockTarget] = useState<CustomTopicModel | null>(null);
  const [lockReasonDraft, setLockReasonDraft] = useState("");
  const [lockReasonVisibleDraft, setLockReasonVisibleDraft] = useState(false);
  const [lockBusy, setLockBusy] = useState(false);
  const [lockInfoTarget, setLockInfoTarget] = useState<CustomTopicModel | null>(null);

  // ── Modal state ───────────────────────────────────────────────
  // approve: edit-then-publish modal | reject: confirm modal
  // create: direct create modal      | edit: topic update modal
  // delete: hard-delete warning modal (ADMIN only, irreversible)
  const [modalType, setModalType] = useState<"approve" | "reject" | "create" | "edit" | "delete" | null>(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState<CustomTopicSuggestionDto | null>(null);
  const [selectedTopic, setSelectedTopic] = useState<CustomTopicModel | null>(null);
  const [titleInput, setTitleInput] = useState("");
  // Optional short note from the admin when rejecting a suggestion
  const [noteInput, setNoteInput] = useState("");
  // Editable slug in the edit modal — sanitized as the admin types
  const [slugInput, setSlugInput] = useState("");
  const [activeInput, setActiveInput] = useState(true);
  // Ordered channel ("kanal") selection in the approve/create/edit modals —
  // up to 10 ids, index 0 = primary channel (empty array = no channel)
  const [channelsInput, setChannelsInput] = useState<number[]>([]);
  // User-proposed NEW channel names shown in the approve modal — the admin
  // can remove them; the kept ones are auto-created on approval
  const [newChannelNamesInput, setNewChannelNamesInput] = useState<string[]>([]);
  const [categories, setCategories] = useState<MdTopicCategoryDto[]>([]);
  // Active topic count per channel id — feeds the count badges in ChannelPicker
  const [channelTopicCounts, setChannelTopicCounts] = useState<Record<number, number>>({});
  // Typeahead state (create modal only)
  const [taMatches, setTaMatches] = useState<string[]>([]);
  const [taExactExists, setTaExactExists] = useState(false);
  const taDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guards against out-of-order typeahead responses
  const taQueryIdRef  = useRef(0);
  const [modalError, setModalError] = useState<string | null>(null);
  const [modalBusy, setModalBusy] = useState(false);

  // ── Fetchers ──────────────────────────────────────────────────
  const fetchPendingCount = useCallback(async () => {
    try {
      setPendingCount(await getPendingSuggestionCount());
    } catch {
      // Badge is cosmetic — ignore fetch errors silently
    }
  }, []);

  const fetchSuggestions = useCallback(async (isRefresh: boolean = false) => {
    if (isRefresh) setSuggestionsRefreshing(true); else setSuggestionsLoading(true);
    setError(null);
    try {
      const res = await getSuggestions({
        page: suggestionsPage,
        size: ADMIN_PAGE_SIZE,
        status: suggestionStatus || undefined,
        categoryId: suggestionCategoryFilter === "" ? undefined : suggestionCategoryFilter,
        suggestedByUsername: suggestionSuggestedByFilter.trim() || undefined,
        dateFrom: suggestionDateFrom || undefined,
        dateTo: suggestionDateTo || undefined,
        reviewedDateFrom: suggestionReviewedDateFrom || undefined,
        reviewedDateTo: suggestionReviewedDateTo || undefined,
        sortBy: suggestionSortField,
        sortDir: suggestionSortDir,
      });
      setSuggestions(res.content);
      setSuggestionsTotalPages(res.totalPages);
      setSuggestionsTotalItems(res.totalItems);
      setSuggestionsLastRefreshedAt(new Date());
    } catch (err: any) {
      setError(err.message || t("admin_ct_fetch_error"));
    } finally {
      setSuggestionsLoading(false);
      setSuggestionsRefreshing(false);
    }
  }, [
    suggestionsPage, suggestionStatus, suggestionCategoryFilter, suggestionSuggestedByFilter,
    suggestionDateFrom, suggestionDateTo, suggestionReviewedDateFrom, suggestionReviewedDateTo,
    suggestionSortField, suggestionSortDir, t,
  ]);

  const fetchTopics = useCallback(async (isRefresh: boolean = false) => {
    if (isRefresh) setTopicsRefreshing(true); else setTopicsLoading(true);
    setError(null);
    try {
      const res = await getAdminTopics({
        page: topicsPage,
        size: ADMIN_PAGE_SIZE,
        search,
        active: topicActiveFilter || undefined,
        categoryId: topicCategoryFilter === "" ? undefined : topicCategoryFilter,
        suggestedByUsername: topicSuggestedByFilter.trim() || undefined,
        createdByUsername: topicCreatedByFilter.trim() || undefined,
        dateFrom: topicDateFrom || undefined,
        dateTo: topicDateTo || undefined,
        suggestedDateFrom: topicSuggestedDateFrom || undefined,
        suggestedDateTo: topicSuggestedDateTo || undefined,
        locked: topicLockedFilter || undefined,
        sortBy: topicSortField,
        sortDir: topicSortDir,
      });
      setTopics(res.content);
      setTopicsTotalPages(res.totalPages);
      setTopicsTotalItems(res.totalItems);
      setTopicsLastRefreshedAt(new Date());
    } catch (err: any) {
      setError(err.message || t("admin_ct_fetch_error"));
    } finally {
      setTopicsLoading(false);
      setTopicsRefreshing(false);
    }
  }, [
    topicsPage, search, topicActiveFilter, topicCategoryFilter, topicSuggestedByFilter,
    topicCreatedByFilter, topicDateFrom, topicDateTo, topicSuggestedDateFrom, topicSuggestedDateTo,
    topicLockedFilter, topicSortField, topicSortDir, t,
  ]);

  useEffect(() => { fetchPendingCount(); }, [fetchPendingCount]);

  // A channel was created inline from a ChannelPicker — append it right away
  // so its chip renders with the proper name, then refetch the authoritative
  // list (the backend shifted display orders for the alphabetical insert)
  const handleCategoryCreated = useCallback((created: MdTopicCategoryDto) => {
    setCategories((prev) =>
      prev.some((c) => c.id === created.id) ? prev : sortChannelsByName([...prev, created]));
    getPublicTopicCategories()
      .then((list) => setCategories(sortChannelsByName(list)))
      .catch(() => {
        // The optimistic append above already keeps the picker usable
      });
  }, []);

  // Active categories for the modal dropdowns — loaded once
  useEffect(() => {
    getPublicTopicCategories()
      .then((list) => setCategories(sortChannelsByName(list)))
      .catch(() => {
        // Category dropdown is optional — fail silently, modals still work
      });
  }, []);

  // Per-channel active topic counts for the picker badges. Re-fetched every
  // time a create/edit/approve modal opens (see the effect below) so counts
  // reflect topics saved earlier in this session.
  const fetchChannelTopicCounts = useCallback(() => {
    customTopicService.getTopicChannels()
      .then((rows) => {
        const map: Record<number, number> = {};
        rows.forEach((r) => { map[r.id] = r.count; });
        setChannelTopicCounts(map);
      })
      .catch(() => {
        // Counts are informational only — badges fall back to 0
      });
  }, []);

  // Only the modals that render a ChannelPicker need fresh counts
  useEffect(() => {
    if (modalType === "approve" || modalType === "create" || modalType === "edit") {
      fetchChannelTopicCounts();
    }
  }, [modalType, fetchChannelTopicCounts]);

  // Debounced title typeahead — create modal only, fires from the 3rd character
  useEffect(() => {
    if (taDebounceRef.current) clearTimeout(taDebounceRef.current);
    const q = titleInput.trim();
    if (modalType !== "create" || q.length < TYPEAHEAD_MIN_CHARS) {
      setTaMatches([]);
      setTaExactExists(false);
      return;
    }
    const queryId = ++taQueryIdRef.current;
    taDebounceRef.current = setTimeout(async () => {
      try {
        const res = await customTopicService.typeahead(q);
        // Ignore stale responses from earlier keystrokes
        if (queryId !== taQueryIdRef.current) return;
        setTaMatches(res.matches);
        setTaExactExists(res.exactExists);
      } catch {
        // Typeahead is a convenience — backend still enforces uniqueness on submit
      }
    }, TYPEAHEAD_DEBOUNCE_MS);
    return () => { if (taDebounceRef.current) clearTimeout(taDebounceRef.current); };
  }, [titleInput, modalType]);
  useEffect(() => { if (innerTab === "suggestions") fetchSuggestions(); }, [innerTab, fetchSuggestions]);
  useEffect(() => { if (innerTab === "topics") fetchTopics(); }, [innerTab, fetchTopics]);

  // ── Modal helpers ─────────────────────────────────────────────
  const closeModal = () => {
    setModalType(null);
    setSelectedSuggestion(null);
    setSelectedTopic(null);
    setTitleInput("");
    setNoteInput("");
    setSlugInput("");
    setActiveInput(true);
    setChannelsInput([]);
    setNewChannelNamesInput([]);
    setTaMatches([]);
    setTaExactExists(false);
    setModalError(null);
    setModalBusy(false);
  };

  const openApproveModal = (s: CustomTopicSuggestionDto) => {
    setSelectedSuggestion(s);
    setTitleInput(s.title);
    // Prefill with the ordered channels the suggesting user picked — the admin
    // can add/remove/reorder them before publishing (index 0 = primary channel)
    setChannelsInput((s.channels ?? []).map((c) => c.id));
    // Prefill the user-proposed NEW channel names — dashed chips the admin can
    // remove; the kept ones are auto-created (alphabetical position) on approval
    setNewChannelNamesInput(s.newChannelNames ?? []);
    setModalType("approve");
  };

  const openRejectModal = (s: CustomTopicSuggestionDto) => {
    setSelectedSuggestion(s);
    setModalType("reject");
  };

  const openCreateModal = () => {
    setTitleInput("");
    setModalType("create");
  };

  const openEditModal = (topic: CustomTopicModel) => {
    setSelectedTopic(topic);
    setTitleInput(topic.title);
    setSlugInput(topic.slug);
    setActiveInput(topic.active);
    setChannelsInput((topic.channels ?? []).map((c) => c.id));
    setModalType("edit");
  };

  // Hard delete is irreversible, so it always goes through the warning modal —
  // never a one-click row action like the active/lock toggles
  const openDeleteModal = (topic: CustomTopicModel) => {
    setSelectedTopic(topic);
    setModalType("delete");
  };

  // ── Actions ───────────────────────────────────────────────────
  const handleApprove = async () => {
    if (!selectedSuggestion) return;
    setModalBusy(true);
    setModalError(null);
    try {
      // Send editedTitle only when the admin actually changed the text.
      // channelsInput is always sent — it is prefilled from the suggestion, so
      // an untouched picker publishes exactly the user's channels.
      const edited = titleInput.trim() !== selectedSuggestion.title.trim() ? titleInput.trim() : undefined;
      // newChannelNamesInput is always sent too — prefilled from the suggestion,
      // so an untouched picker publishes exactly the user's proposals
      await approveSuggestion(selectedSuggestion.id, edited, channelsInput, newChannelNamesInput);
      setSuccessMessage(t("admin_ct_approved_success"));
      closeModal();
      fetchSuggestions();
      fetchPendingCount();
    } catch (err: any) {
      setModalError(parseApiError(err.message || "", t));
      setModalBusy(false);
    }
  };

  const handleReject = async () => {
    if (!selectedSuggestion) return;
    setModalBusy(true);
    setModalError(null);
    try {
      await rejectSuggestion(selectedSuggestion.id, noteInput.trim() || undefined);
      setSuccessMessage(t("admin_ct_rejected_success"));
      closeModal();
      fetchSuggestions();
      fetchPendingCount();
    } catch (err: any) {
      setModalError(parseApiError(err.message || "", t));
      setModalBusy(false);
    }
  };

  const handleCreate = async () => {
    setModalBusy(true);
    setModalError(null);
    try {
      await createTopic(titleInput.trim(), channelsInput);
      setSuccessMessage(t("admin_ct_created_success"));
      closeModal();
      fetchTopics();
    } catch (err: any) {
      setModalError(parseApiError(err.message || "", t));
      setModalBusy(false);
    }
  };

  const handleEditSave = async () => {
    if (!selectedTopic) return;
    setModalBusy(true);
    setModalError(null);
    try {
      // channelIds semantics: undefined = unchanged, [] = clear all channels,
      // otherwise = replace with the picker's ordered list. Only sent when the
      // admin actually changed the selection (order changes count too).
      const oldChannelIds = (selectedTopic.channels ?? []).map((c) => c.id);
      const channelsChanged = JSON.stringify(channelsInput) !== JSON.stringify(oldChannelIds);
      await updateTopic(selectedTopic.id, {
        title: titleInput.trim() !== selectedTopic.title ? titleInput.trim() : undefined,
        // Slug is only sent when the admin actually changed it
        slug: slugInput.trim() !== selectedTopic.slug ? slugInput.trim() : undefined,
        active: activeInput !== selectedTopic.active ? activeInput : undefined,
        channelIds: channelsChanged ? channelsInput : undefined,
      });
      setSuccessMessage(t("admin_ct_updated_success"));
      closeModal();
      fetchTopics();
    } catch (err: any) {
      setModalError(parseApiError(err.message || "", t));
      setModalBusy(false);
    }
  };

  // Hard delete — physically removes the topic and everything under it.
  // Only reachable from the warning modal, and only for admins.
  const handleDelete = async () => {
    if (!selectedTopic) return;
    setModalBusy(true);
    setModalError(null);
    try {
      await deleteTopic(selectedTopic.id);
      setSuccessMessage(t("admin_ct_deleted_success", { title: selectedTopic.title }));
      closeModal();
      // The deleted row may have been the last one on the current page — go back
      // to page 0 so the table never lands on an empty page
      setTopicsPage(0);
      fetchTopics();
    } catch (err: any) {
      setModalError(parseApiError(err.message || "", t));
      setModalBusy(false);
    }
  };

  // Quick-toggle active status directly from the table row
  // (same pattern as clubs/people/teams admin panels)
  const handleToggleActive = async (topic: CustomTopicModel) => {
    try {
      await updateTopic(topic.id, { active: !topic.active });
      setSuccessMessage(
        !topic.active
          ? t("admin_toggle_activated", { name: topic.title })
          : t("admin_toggle_deactivated", { name: topic.title }),
      );
      fetchTopics();
    } catch (err: any) {
      setError(err.message || t("admin_toggle_error"));
    }
  };

  // Quick unlock straight from the table row. Locking opens the reason modal
  // instead (see handleConfirmLock) so the admin can leave an optional note.
  const handleToggleLock = async (topic: CustomTopicModel) => {
    if (!topic.isLocked) {
      // About to lock — collect an optional reason first
      setLockTarget(topic);
      setLockReasonDraft("");
      setLockReasonVisibleDraft(false);
      return;
    }
    try {
      await updateTopic(topic.id, { isLocked: false });
      setSuccessMessage(t("admin_ct_updated_success"));
      fetchTopics();
    } catch (err: any) {
      setError(parseApiError(err.message || "", t));
    }
  };

  const handleConfirmLock = async () => {
    if (!lockTarget) return;
    setLockBusy(true);
    try {
      await updateTopic(lockTarget.id, {
        isLocked: true,
        lockReason: lockReasonDraft.trim() || undefined,
        lockReasonVisible: lockReasonVisibleDraft,
      });
      setSuccessMessage(t("admin_ct_updated_success"));
      setLockTarget(null);
      fetchTopics();
    } catch (err: any) {
      setError(parseApiError(err.message || "", t));
    } finally {
      setLockBusy(false);
    }
  };

  const handleCancelLock = () => {
    setLockTarget(null);
    setLockReasonDraft("");
    setLockReasonVisibleDraft(false);
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setTopicsPage(0);
  };

  // Clear-X inside the search box — resets the input AND the applied search
  // term, so the table immediately re-fetches unfiltered results.
  const handleClearSearch = () => {
    setSearchInput("");
    setSearch("");
    setTopicsPage(0);
  };

  // "Temizle" (clear filters) — same convention as AdminClubs/AdminUsers etc.
  const hasActiveTopicFilters =
    topicActiveFilter !== "" || topicCategoryFilter !== "" ||
    topicSuggestedByFilter !== "" || topicCreatedByFilter !== "" ||
    topicDateFrom !== "" || topicDateTo !== "" ||
    topicSuggestedDateFrom !== "" || topicSuggestedDateTo !== "" || topicLockedFilter !== "";
  const handleClearTopicFilters = () => {
    setTopicActiveFilter("");
    setTopicCategoryFilter("");
    setTopicSuggestedByFilter("");
    setTopicCreatedByFilter("");
    setTopicDateFrom("");
    setTopicDateTo("");
    setTopicSuggestedDateFrom("");
    setTopicSuggestedDateTo("");
    setTopicLockedFilter("");
    setTopicsPage(0);
  };

  const hasActiveSuggestionFilters =
    suggestionStatus !== "" || suggestionCategoryFilter !== "" ||
    suggestionSuggestedByFilter !== "" || suggestionDateFrom !== "" || suggestionDateTo !== "" ||
    suggestionReviewedDateFrom !== "" || suggestionReviewedDateTo !== "";
  const handleClearSuggestionFilters = () => {
    setSuggestionStatus("");
    setSuggestionCategoryFilter("");
    setSuggestionSuggestedByFilter("");
    setSuggestionDateFrom("");
    setSuggestionDateTo("");
    setSuggestionReviewedDateFrom("");
    setSuggestionReviewedDateTo("");
    setSuggestionsPage(0);
  };

  // Clicking a new column starts it at its natural direction (see
  // defaultTopicSortDirection); clicking the already-active column flips it.
  // Unlike AdminRankBoard (fully loaded, sorted client-side), this table is
  // server-paginated, so a sort change re-fetches page 0 from the backend.
  const handleTopicSort = (field: TopicSortField) => {
    if (field === topicSortField) {
      setTopicSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setTopicSortField(field);
      setTopicSortDir(defaultTopicSortDirection(TOPIC_SORT_COLUMN_TYPES[field]));
    }
    setTopicsPage(0);
  };

  // Neutral / ascending / descending indicator — same icon set already
  // used across the other admin panels (Users, Clubs, Teams, People, ...).
  const getTopicSortIcon = (field: TopicSortField) => {
    if (topicSortField !== field) return <FaSort className="sort-icon" />;
    return topicSortDir === "asc" ? (
      <FaSortUp className="sort-icon active" />
    ) : (
      <FaSortDown className="sort-icon active" />
    );
  };

  // Same sort mechanism as the topics table above, applied to the suggestions
  // table (also server-paginated, so a sort change re-fetches page 0).
  const handleSuggestionSort = (field: SuggestionSortField) => {
    if (field === suggestionSortField) {
      setSuggestionSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSuggestionSortField(field);
      setSuggestionSortDir(defaultTopicSortDirection(SUGGESTION_SORT_COLUMN_TYPES[field]));
    }
    setSuggestionsPage(0);
  };

  const getSuggestionSortIcon = (field: SuggestionSortField) => {
    if (suggestionSortField !== field) return <FaSort className="sort-icon" />;
    return suggestionSortDir === "asc" ? (
      <FaSortUp className="sort-icon active" />
    ) : (
      <FaSortDown className="sort-icon active" />
    );
  };

  // ── Render helpers ────────────────────────────────────────────
  const statusBadge = (status: SuggestionStatus) => {
    const cls = status === "PENDING" ? "pending" : status === "APPROVED" ? "approved" : "rejected";
    const label = status === "PENDING"
      ? t("admin_ct_status_pending")
      : status === "APPROVED"
      ? t("admin_ct_status_approved")
      : t("admin_ct_status_rejected");
    return <span className={`ct-status-badge ${cls}`}>{label}</span>;
  };

  return (
    <div className="admin-custom-topics">
      <div className="admin-section-header">
        <h2><FaHashtag /> {t("admin_tab_custom_topics")}</h2>
      </div>

      {successMessage && <AdminSuccessBanner message={successMessage} onDismiss={clearSuccess} />}
      {error && <AdminErrorBanner message={error} />}

      {/* ── Inner tab bar: Öneriler | Başlıklar ─────────────── */}
      <div className="ct-inner-tabs">
        <button
          className={`ct-inner-tab ${innerTab === "suggestions" ? "active" : ""}`}
          onClick={() => setInnerTab("suggestions")}
        >
          {t("admin_ct_suggestions_tab")}
          {pendingCount > 0 && <span className="ct-pending-badge">{pendingCount}</span>}
        </button>
        <button
          className={`ct-inner-tab ${innerTab === "topics" ? "active" : ""}`}
          onClick={() => setInnerTab("topics")}
        >
          {t("admin_ct_topics_tab")}
        </button>
      </div>

      {/* ══ SUGGESTIONS TAB ══════════════════════════════════ */}
      {innerTab === "suggestions" && (
        <>
          <div className="ct-toolbar">
            <div className="ct-toolbar-left">
              <button
                className={`filter-toggle ${showSuggestionFilters ? "active" : ""}`}
                onClick={() => setShowSuggestionFilters(!showSuggestionFilters)}
              >
                <FaFilter /> {t("admin_filters")}
                <FaChevronDown className={`chevron ${showSuggestionFilters ? "open" : ""}`} />
              </button>
              {hasActiveSuggestionFilters && (
                <button className="clear-filters" onClick={handleClearSuggestionFilters}>
                  <FaTimes /> {t("admin_clear_filters")}
                </button>
              )}
            </div>
            <div className="ct-toolbar-right">
              {suggestionsLastRefreshedAt && (
                <span className="ct-last-refreshed">
                  {t("admin_ct_last_refreshed", { time: formatRefreshTime(suggestionsLastRefreshedAt) })}
                </span>
              )}
              <AdminRefreshButton
                onClick={() => fetchSuggestions(true)}
                refreshing={suggestionsRefreshing}
                disabled={suggestionsLoading}
                label={t("admin_ct_refresh")}
              />
            </div>
          </div>

          {/* Filter panel — open by default, same convention as the other admin panels */}
          {showSuggestionFilters && (
            <div className="admin-filter-panel">
              <div className="admin-filter-group">
                <label>{t("admin_status_label")}</label>
                <select
                  value={suggestionStatus}
                  onChange={(e) => { setSuggestionStatus(e.target.value as SuggestionStatus | ""); setSuggestionsPage(0); }}
                >
                  <option value="PENDING">{t("admin_ct_status_pending")}</option>
                  <option value="APPROVED">{t("admin_ct_status_approved")}</option>
                  <option value="REJECTED">{t("admin_ct_status_rejected")}</option>
                  <option value="">{t("admin_ct_status_all")}</option>
                </select>
              </div>
              <div className="admin-filter-group">
                <label>{t("topic_category_label")}</label>
                <select
                  value={suggestionCategoryFilter}
                  onChange={(e) => {
                    setSuggestionCategoryFilter(e.target.value ? Number(e.target.value) : "");
                    setSuggestionsPage(0);
                  }}
                >
                  <option value="">{t("admin_ct_all_categories")}</option>
                  <option value="0">{t("admin_ct_no_category")}</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="admin-filter-group">
                <label>{t("admin_ct_suggested_by")}</label>
                <input
                  type="text"
                  placeholder={t("admin_filter_author_name_placeholder", "kullanıcı adı")}
                  value={suggestionSuggestedByFilter}
                  onChange={(e) => { setSuggestionSuggestedByFilter(e.target.value); setSuggestionsPage(0); }}
                />
              </div>
              <div className="admin-filter-group">
                <label>{t("admin_ct_suggested_date_from")}</label>
                <input
                  type="date"
                  value={suggestionDateFrom}
                  onChange={(e) => { setSuggestionDateFrom(e.target.value); setSuggestionsPage(0); }}
                />
              </div>
              <div className="admin-filter-group">
                <label>{t("admin_ct_suggested_date_to")}</label>
                <input
                  type="date"
                  value={suggestionDateTo}
                  onChange={(e) => { setSuggestionDateTo(e.target.value); setSuggestionsPage(0); }}
                />
              </div>
              <div className="admin-filter-group">
                <label>{t("admin_ct_accepted_date_from")}</label>
                <input
                  type="date"
                  value={suggestionReviewedDateFrom}
                  onChange={(e) => { setSuggestionReviewedDateFrom(e.target.value); setSuggestionsPage(0); }}
                />
              </div>
              <div className="admin-filter-group">
                <label>{t("admin_ct_accepted_date_to")}</label>
                <input
                  type="date"
                  value={suggestionReviewedDateTo}
                  onChange={(e) => { setSuggestionReviewedDateTo(e.target.value); setSuggestionsPage(0); }}
                />
              </div>
            </div>
          )}

          {suggestionsLoading && suggestions.length === 0 ? (
            <div className="ct-loading">{t("admin_loading", "Yükleniyor...")}</div>
          ) : suggestions.length === 0 ? (
            <div className="ct-empty">{t("admin_ct_no_suggestions")}</div>
          ) : (
            <div className={`ct-table-wrapper ${suggestionsLoading ? "ct-table-refreshing" : ""}`}>
              <table className="ct-table ct-suggestions-table">
                <thead>
                  <tr>
                    <th className="sortable-col ct-col-id" onClick={() => handleSuggestionSort("id")}>
                      ID {getSuggestionSortIcon("id")}
                    </th>
                    <th className="sortable-col ct-col-title" onClick={() => handleSuggestionSort("title")}>
                      {t("admin_ct_title_label")} {getSuggestionSortIcon("title")}
                    </th>
                    <th className="ct-col-channel">{t("topic_category_label")}</th>
                    <th
                      className="sortable-col ct-col-suggested-by"
                      onClick={() => handleSuggestionSort("suggestedByUsername")}
                    >
                      {t("admin_ct_suggested_by")} {getSuggestionSortIcon("suggestedByUsername")}
                    </th>
                    <th className="sortable-col ct-col-date" onClick={() => handleSuggestionSort("createdAt")}>
                      {t("admin_ct_suggested_at")} {getSuggestionSortIcon("createdAt")}
                    </th>
                    <th className="sortable-col ct-col-date" onClick={() => handleSuggestionSort("reviewedAt")}>
                      {t("admin_ct_accepted_at")} {getSuggestionSortIcon("reviewedAt")}
                    </th>
                    <th className="sortable-col ct-col-days-diff" onClick={() => handleSuggestionSort("daysDiff")}>
                      {t("admin_ct_days_diff")} {getSuggestionSortIcon("daysDiff")}
                    </th>
                    <th className="ct-col-status">{t("admin_ct_status")}</th>
                    <th className="ct-col-actions">{t("admin_ct_actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {suggestions.map((s) => (
                    <tr key={s.id}>
                      <td>{s.id}</td>
                      <td className="ct-title-cell">
                        <span className="ct-suggestion-title-text" title={s.title}>{s.title}</span>
                        {/* Admin edited the title before publishing — show what went live and who edited it */}
                        {s.publishedTitle && s.publishedTitle !== s.title && (
                          <div className="ct-published-as">
                            → {s.publishedTitle} ({s.reviewedByUsername ?? "—"})
                          </div>
                        )}
                        {/* Rejection (or other review) note from the admin */}
                        {s.adminNote && (
                          <div className="ct-admin-note">{s.adminNote}</div>
                        )}
                      </td>
                      {/* All channels of the suggestion, in order (primary first).
                          User-proposed NEW channel names are appended with a "+"
                          prefix so pending creations stand out in the list. */}
                      <td
                        className="ct-channel-cell"
                        title={(s.channels?.length || s.newChannelNames?.length)
                          ? [...(s.channels ?? []).map((c) => c.name),
                             ...(s.newChannelNames ?? []).map((n) => `+${n}`)].join(", ")
                          : undefined}
                      >
                        {(s.channels?.length || s.newChannelNames?.length)
                          ? [...(s.channels ?? []).map((c) => c.name),
                             ...(s.newChannelNames ?? []).map((n) => `+${n}`)].join(", ")
                          : "—"}
                      </td>
                      <td
                        className="ct-col-suggested-by"
                        title={s.suggestedByUsername ?? undefined}
                      >
                        {s.suggestedByUsername && s.suggestedByUserId ? (
                          <Link to={`/yazar/${s.suggestedByUsername}--${s.suggestedByUserId}`}>
                            {s.suggestedByUsername}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>{formatAdminDate(s.createdAt)}</td>
                      <td>{formatAdminDate(s.reviewedAt)}</td>
                      <td>{formatDaysDiff(s.createdAt, s.reviewedAt)}</td>
                      <td>{statusBadge(s.status)}</td>
                      <td>
                        {s.status === "PENDING" ? (
                          <div className="action-buttons">
                            <button
                              className="action-btn success"
                              title={t("admin_ct_approve")}
                              onClick={() => openApproveModal(s)}
                            >
                              <FaCheck />
                            </button>
                            <button
                              className="action-btn danger"
                              title={t("admin_ct_reject")}
                              onClick={() => openRejectModal(s)}
                            >
                              <FaBan />
                            </button>
                          </div>
                        ) : (
                          <span className="ct-reviewed-by">{s.reviewedByUsername ?? "—"}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {suggestionsTotalPages > 1 && (
            <div className="ct-pagination">
              <Pagination
                currentPage={suggestionsPage + 1}
                totalPages={suggestionsTotalPages}
                totalItems={suggestionsTotalItems}
                paginate={(p: number) => setSuggestionsPage(p - 1)}
              />
            </div>
          )}
        </>
      )}

      {/* ══ TOPICS TAB ═══════════════════════════════════════ */}
      {innerTab === "topics" && (
        <>
          <div className="ct-toolbar">
            <div className="ct-toolbar-left">
              <div className="ct-search">
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
                  placeholder={t("admin_ct_search_placeholder")}
                />
                {/* Clear-X — shown as soon as there's anything to clear (typed
                    text or an applied search); clears both and re-fetches. */}
                {(searchInput !== "" || search !== "") && (
                  <button
                    className="ct-search-clear-btn"
                    onClick={handleClearSearch}
                    title={t("admin_clear_filters")}
                  >
                    <FaTimes />
                  </button>
                )}
                <button className="ct-search-btn" onClick={handleSearch} title={t("admin_search", "Ara")}>
                  <FaSearch />
                </button>
              </div>
              <button
                className={`filter-toggle ${showTopicFilters ? "active" : ""}`}
                onClick={() => setShowTopicFilters(!showTopicFilters)}
              >
                <FaFilter /> {t("admin_filters")}
                <FaChevronDown className={`chevron ${showTopicFilters ? "open" : ""}`} />
              </button>
              {hasActiveTopicFilters && (
                <button className="clear-filters" onClick={handleClearTopicFilters}>
                  <FaTimes /> {t("admin_clear_filters")}
                </button>
              )}
            </div>
            <div className="ct-toolbar-right">
              {topicsLastRefreshedAt && (
                <span className="ct-last-refreshed">
                  {t("admin_ct_last_refreshed", { time: formatRefreshTime(topicsLastRefreshedAt) })}
                </span>
              )}
              <AdminRefreshButton
                onClick={() => fetchTopics(true)}
                refreshing={topicsRefreshing}
                disabled={topicsLoading}
                label={t("admin_ct_refresh")}
              />
              <button className="ct-create-btn" onClick={openCreateModal}>
                <FaPlus /> {t("admin_ct_create")}
              </button>
            </div>
          </div>

          {/* Filter panel — open by default, same convention as the other admin panels */}
          {showTopicFilters && (
            <div className="admin-filter-panel">
              <div className="admin-filter-group">
                <label>{t("admin_status_label")}</label>
                <select
                  value={topicActiveFilter}
                  onChange={(e) => {
                    setTopicActiveFilter(e.target.value as "" | "true" | "false");
                    setTopicsPage(0);
                  }}
                >
                  <option value="">{t("admin_all")}</option>
                  <option value="true">{t("admin_active")}</option>
                  <option value="false">{t("admin_inactive")}</option>
                </select>
              </div>
              <div className="admin-filter-group">
                <label>{t("topic_category_label")}</label>
                <select
                  value={topicCategoryFilter}
                  onChange={(e) => {
                    setTopicCategoryFilter(e.target.value ? Number(e.target.value) : "");
                    setTopicsPage(0);
                  }}
                >
                  <option value="">{t("admin_ct_all_categories")}</option>
                  <option value="0">{t("admin_ct_no_category")}</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="admin-filter-group">
                <label>{t("admin_ct_suggested_by")}</label>
                <input
                  type="text"
                  placeholder={t("admin_filter_author_name_placeholder", "kullanıcı adı")}
                  value={topicSuggestedByFilter}
                  onChange={(e) => { setTopicSuggestedByFilter(e.target.value); setTopicsPage(0); }}
                />
              </div>
              <div className="admin-filter-group">
                <label>{t("admin_ct_created_by")}</label>
                <input
                  type="text"
                  placeholder={t("admin_filter_author_name_placeholder", "kullanıcı adı")}
                  value={topicCreatedByFilter}
                  onChange={(e) => { setTopicCreatedByFilter(e.target.value); setTopicsPage(0); }}
                />
              </div>
              <div className="admin-filter-group">
                <label>{t("admin_ct_suggested_date_from")}</label>
                <input
                  type="date"
                  value={topicSuggestedDateFrom}
                  onChange={(e) => { setTopicSuggestedDateFrom(e.target.value); setTopicsPage(0); }}
                />
              </div>
              <div className="admin-filter-group">
                <label>{t("admin_ct_suggested_date_to")}</label>
                <input
                  type="date"
                  value={topicSuggestedDateTo}
                  onChange={(e) => { setTopicSuggestedDateTo(e.target.value); setTopicsPage(0); }}
                />
              </div>
              <div className="admin-filter-group">
                <label>{t("admin_ct_accepted_date_from")}</label>
                <input
                  type="date"
                  value={topicDateFrom}
                  onChange={(e) => { setTopicDateFrom(e.target.value); setTopicsPage(0); }}
                />
              </div>
              <div className="admin-filter-group">
                <label>{t("admin_ct_accepted_date_to")}</label>
                <input
                  type="date"
                  value={topicDateTo}
                  onChange={(e) => { setTopicDateTo(e.target.value); setTopicsPage(0); }}
                />
              </div>
              <div className="admin-filter-group">
                <label>{t("admin_ct_lock_status")}</label>
                <select
                  value={topicLockedFilter}
                  onChange={(e) => {
                    setTopicLockedFilter(e.target.value as "" | "true" | "false");
                    setTopicsPage(0);
                  }}
                >
                  <option value="">{t("admin_all")}</option>
                  <option value="true">{t("admin_ct_locked")}</option>
                  <option value="false">{t("admin_ct_unlocked")}</option>
                </select>
              </div>
            </div>
          )}

          {topicsLoading && topics.length === 0 ? (
            <div className="ct-loading">{t("admin_loading", "Yükleniyor...")}</div>
          ) : topics.length === 0 ? (
            <div className="ct-empty">{t("admin_ct_no_topics")}</div>
          ) : (
            <div className={`ct-table-wrapper ${topicsLoading ? "ct-table-refreshing" : ""}`}>
              <table className="ct-table ct-topics-table">
                <thead>
                  <tr>
                    <th className="sortable-col ct-col-id" onClick={() => handleTopicSort("id")}>
                      ID {getTopicSortIcon("id")}
                    </th>
                    {/* TOPICS_COLUMN_WIDTHS: the three columns whose share changed when
                        the hard-delete button was added are pinned INLINE, not only in
                        AdminCustomTopics.css. The actions cell now holds up to four
                        buttons and gets clipped at the old 6%; an inline width beats any
                        stale stylesheet a browser or service worker may still be serving,
                        so the delete button can never disappear off the right edge again.
                        The percentages here and in the CSS are kept identical and still
                        total exactly 100% — table-layout is fixed. */}
                    <th
                      className="sortable-col ct-col-title"
                      style={{ width: TOPICS_COLUMN_WIDTHS.title }}
                      onClick={() => handleTopicSort("title")}
                    >
                      {t("admin_ct_title_label")} {getTopicSortIcon("title")}
                    </th>
                    <th className="ct-col-slug" style={{ width: TOPICS_COLUMN_WIDTHS.slug }}>Slug</th>
                    <th className="ct-col-channel">{t("topic_category_label")}</th>
                    <th
                      className="sortable-col ct-col-entry-count"
                      onClick={() => handleTopicSort("entryCount")}
                    >
                      {t("admin_ct_entry_count")} {getTopicSortIcon("entryCount")}
                    </th>
                    <th
                      className="sortable-col ct-col-suggested-by"
                      onClick={() => handleTopicSort("suggestedByUsername")}
                    >
                      {t("admin_ct_suggested_by")} {getTopicSortIcon("suggestedByUsername")}
                    </th>
                    <th
                      className="sortable-col ct-col-created-by"
                      onClick={() => handleTopicSort("createdByUsername")}
                    >
                      {t("admin_ct_created_by")} {getTopicSortIcon("createdByUsername")}
                    </th>
                    <th
                      className="sortable-col ct-col-suggested-at"
                      onClick={() => handleTopicSort("suggestedAt")}
                    >
                      {t("admin_ct_suggested_at")} {getTopicSortIcon("suggestedAt")}
                    </th>
                    <th className="sortable-col ct-col-date" onClick={() => handleTopicSort("createdAt")}>
                      {t("admin_ct_accepted_at")} {getTopicSortIcon("createdAt")}
                    </th>
                    <th
                      className="sortable-col ct-col-days-diff"
                      onClick={() => handleTopicSort("daysDiff")}
                    >
                      {t("admin_ct_days_diff")} {getTopicSortIcon("daysDiff")}
                    </th>
                    <th className="ct-col-status">{t("admin_ct_status")}</th>
                    <th className="ct-col-actions" style={{ width: TOPICS_COLUMN_WIDTHS.actions }}>
                      {t("admin_ct_actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {topics.map((topic) => (
                    <tr
                      key={topic.id}
                      // Nickaltı rows get a distinct background so they stand out
                      // from normal topics at a glance in the admin list
                      className={`${!topic.active ? "ct-row-inactive" : ""} ${topic.ownerUserId ? "ct-row-nickalti" : ""}`.trim()}
                    >
                      <td>{topic.id}</td>
                      {/* Title links to the public topic page — same target as the slug column */}
                      <td className="ct-title-cell ct-topics-title-cell" title={topic.title}>
                        <Link to={`/gundem/${topic.slug}`}>{topic.title}</Link>
                      </td>
                      <td className="ct-slug-cell" title={`/gundem/${topic.slug}`}>/gundem/{topic.slug}</td>
                      {/* All channels of the topic, in order (primary first) */}
                      <td
                        className="ct-channel-cell"
                        title={topic.channels?.length ? topic.channels.map((c) => c.name).join(", ") : undefined}
                      >
                        {topic.channels?.length ? topic.channels.map((c) => c.name).join(", ") : "—"}
                      </td>
                      <td className="ct-col-entry-count">{topic.entryCount ?? 0}</td>
                      <td
                        className="ct-col-suggested-by"
                        title={topic.suggestedByUsername ?? undefined}
                      >
                        {topic.suggestedByUsername && topic.suggestedByUserId ? (
                          <Link to={`/yazar/${topic.suggestedByUsername}--${topic.suggestedByUserId}`}>
                            {topic.suggestedByUsername}
                          </Link>
                        ) : (
                          topic.ownerUserId ? t("admin_ct_auto_generated") : t("admin_ct_direct")
                        )}
                      </td>
                      <td
                        className="ct-col-created-by"
                        title={topic.createdByUsername ?? undefined}
                      >
                        {topic.createdByUsername && topic.createdByUserId ? (
                          <Link to={`/yazar/${topic.createdByUsername}--${topic.createdByUserId}`}>
                            {topic.createdByUsername}
                          </Link>
                        ) : (
                          topic.ownerUserId ? t("admin_ct_auto_generated") : "—"
                        )}
                      </td>
                      <td className="ct-col-suggested-at">{formatAdminDate(topic.suggestedAt ?? null)}</td>
                      <td>{formatAdminDate(topic.createdAt)}</td>
                      <td className="ct-col-days-diff">{formatDaysDiff(topic.suggestedAt, topic.createdAt)}</td>
                      <td>
                        {/* Quick-toggle active directly from row */}
                        <button
                          className={`active-toggle-btn ${topic.active ? "is-active" : "is-inactive"}`}
                          title={
                            topic.active
                              ? t("admin_toggle_active_title")
                              : t("admin_toggle_inactive_title")
                          }
                          onClick={() => handleToggleActive(topic)}
                        >
                          {topic.active ? (
                            <>
                              <FaToggleOn /> {t("admin_active")}
                            </>
                          ) : (
                            <>
                              <FaToggleOff /> {t("admin_inactive")}
                            </>
                          )}
                        </button>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="action-btn"
                            title={t("admin_ct_edit")}
                            onClick={() => openEditModal(topic)}
                          >
                            <FaEdit />
                          </button>
                          <button
                            className={`action-btn ${topic.isLocked ? "lock-active" : "lock"}`}
                            title={topic.isLocked ? t("admin_ct_unlock") : t("admin_ct_lock")}
                            onClick={() => handleToggleLock(topic)}
                          >
                            {topic.isLocked ? <FaUnlock /> : <FaLock />}
                          </button>
                          {topic.isLocked && (
                            <button
                              className="action-btn"
                              title={t("admin_lock_info_title")}
                              onClick={() => setLockInfoTarget(topic)}
                            >
                              <FaInfoCircle />
                            </button>
                          )}
                          {/* Hard delete — admins only, and never for nickaltı
                              topics (the backend rejects those as protected) */}
                          {isAdmin && !topic.ownerUserId && (
                            <button
                              className="action-btn danger"
                              title={t("admin_ct_delete")}
                              onClick={() => openDeleteModal(topic)}
                            >
                              <FaTrash />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {topicsTotalPages > 1 && (
            <div className="ct-pagination">
              <Pagination
                currentPage={topicsPage + 1}
                totalPages={topicsTotalPages}
                totalItems={topicsTotalItems}
                paginate={(p: number) => setTopicsPage(p - 1)}
              />
            </div>
          )}
        </>
      )}

      {/* ── Approve Modal (title editable before publish) ────── */}
      {modalType === "approve" && selectedSuggestion && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h3>{t("admin_ct_approve_title")}</h3>
              <button className="modal-close-btn" onClick={closeModal}><FaTimes /></button>
            </div>
            <div className="admin-modal-body">
              {modalError && <div className="modal-error">{modalError}</div>}
              <p className="ct-modal-hint">{t("admin_ct_approve_hint")}</p>
              <div className="form-group">
                <label>{t("admin_ct_title_label")}</label>
                <input
                  type="text"
                  value={titleInput}
                  // Topic titles are lowercase-only (sözlük convention)
                  onChange={(e) => setTitleInput(e.target.value.toLocaleLowerCase("tr"))}
                  maxLength={MAX_TITLE_LENGTH}
                  autoFocus
                />
                <div className="ct-char-counter">{titleInput.length} / {MAX_TITLE_LENGTH}</div>
              </div>
              {/* Channels ("kanal") — ordered multi selection, up to 10;
                  index 0 is the primary channel (list icon). Dashed chips are
                  the user's NEW channel proposals — kept ones are created
                  automatically (alphabetical master data position) on approval. */}
              <div className="form-group">
                <label>{t("topic_channels_label", "Kanallar")}</label>
                <ChannelPicker
                  value={channelsInput}
                  onChange={setChannelsInput}
                  categories={categories}
                  proposedNames={newChannelNamesInput}
                  onProposedNamesChange={setNewChannelNamesInput}
                  canCreateChannel={isAdmin}
                  onCategoryCreated={handleCategoryCreated}
                  topicCounts={channelTopicCounts}
                />
                {newChannelNamesInput.length > 0 && (
                  <div className="ct-new-channels-hint">
                    {t("admin_ct_new_channels_hint", "Kesikli çerçeveli kanallar kullanıcının önerdiği yeni kanallardır; onayla birlikte otomatik oluşturulur.")}
                  </div>
                )}
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="modal-cancel-btn" onClick={closeModal}>{t("admin_cancel")}</button>
              <button
                className="modal-confirm-btn"
                onClick={handleApprove}
                disabled={modalBusy || !titleInput.trim()}
              >
                <FaCheck /> {t("admin_ct_approve")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject Modal (confirm) ───────────────────────────── */}
      {modalType === "reject" && selectedSuggestion && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h3>{t("admin_ct_reject_title")}</h3>
              <button className="modal-close-btn" onClick={closeModal}><FaTimes /></button>
            </div>
            <div className="admin-modal-body">
              {modalError && <div className="modal-error">{modalError}</div>}
              <p>{t("admin_ct_reject_warning")}</p>
              <div className="ct-reject-target">{selectedSuggestion.title}</div>
              {/* Optional short note from the reviewing admin — stored with the suggestion */}
              <div className="form-group" style={{ marginTop: 10 }}>
                <label>{t("admin_ct_note")}</label>
                <textarea
                  className="ct-note-input"
                  value={noteInput}
                  onChange={(e) => setNoteInput(e.target.value)}
                  placeholder={t("admin_ct_note_placeholder")}
                  maxLength={ADMIN_ADMIN_NOTE_MAX_LENGTH}
                  rows={2}
                />
                <AdminCharCounter value={noteInput} max={ADMIN_ADMIN_NOTE_MAX_LENGTH} />
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="modal-cancel-btn" onClick={closeModal}>{t("admin_cancel")}</button>
              <button className="modal-delete-btn" onClick={handleReject} disabled={modalBusy}>
                <FaBan /> {t("admin_ct_reject")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create Modal (admin direct create) ───────────────── */}
      {modalType === "create" && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h3>{t("admin_ct_create_title")}</h3>
              <button className="modal-close-btn" onClick={closeModal}><FaTimes /></button>
            </div>
            <div className="admin-modal-body">
              {modalError && <div className="modal-error">{modalError}</div>}
              <div className="form-group">
                <label>{t("admin_ct_title_label")}</label>
                <input
                  type="text"
                  value={titleInput}
                  // Topic titles are lowercase-only (sözlük convention)
                  onChange={(e) => setTitleInput(e.target.value.toLocaleLowerCase("tr"))}
                  maxLength={MAX_TITLE_LENGTH}
                  autoFocus
                />
                <div className="ct-char-counter">{titleInput.length} / {MAX_TITLE_LENGTH}</div>
                {/* Exact duplicate → warning; otherwise list similar existing titles */}
                {taExactExists ? (
                  <div className="ct-duplicate-warning">
                    {t("suggest_topic_exact_exists")}
                  </div>
                ) : taMatches.length > 0 && (
                  <div className="ct-typeahead-matches">
                    <div className="ct-typeahead-label">{t("suggest_topic_similar")}</div>
                    {taMatches.map((m) => (
                      <div key={m} className="ct-typeahead-match">{m}</div>
                    ))}
                  </div>
                )}
              </div>
              {/* Channels ("kanal") — ordered multi selection, up to 10;
                  index 0 is the primary channel (list icon) */}
              <div className="form-group">
                <label>{t("topic_channels_label", "Kanallar")}</label>
                <ChannelPicker
                  value={channelsInput}
                  onChange={setChannelsInput}
                  categories={categories}
                  canCreateChannel={isAdmin}
                  onCategoryCreated={handleCategoryCreated}
                  topicCounts={channelTopicCounts}
                />
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="modal-cancel-btn" onClick={closeModal}>{t("admin_cancel")}</button>
              <button
                className="modal-confirm-btn"
                onClick={handleCreate}
                disabled={modalBusy || !titleInput.trim() || taExactExists}
              >
                <FaCheck /> {t("admin_ct_create")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Modal (title + active; slug never changes) ──── */}
      {modalType === "edit" && selectedTopic && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h3>{t("admin_ct_edit_title")}</h3>
              <button className="modal-close-btn" onClick={closeModal}><FaTimes /></button>
            </div>
            <div className="admin-modal-body">
              {modalError && <div className="modal-error">{modalError}</div>}
              <div className="form-group">
                <label>{t("admin_ct_title_label")}</label>
                <input
                  type="text"
                  value={titleInput}
                  // Topic titles are lowercase-only (sözlük convention)
                  onChange={(e) => setTitleInput(e.target.value.toLocaleLowerCase("tr"))}
                  maxLength={MAX_TITLE_LENGTH}
                  autoFocus
                />
                <div className="ct-char-counter">{titleInput.length} / {MAX_TITLE_LENGTH}</div>
              </div>
              {/* Slug is editable by the admin — never auto-regenerated on title
                  changes, but a deliberate manual fix is allowed. Sanitized as
                  typed: lowercase, spaces → dashes, only [a-z0-9-]. */}
              <div className="form-group">
                <label>Slug</label>
                <div className="ct-slug-edit">
                  <span className="ct-slug-prefix">/gundem/</span>
                  <input
                    type="text"
                    value={slugInput}
                    onChange={(e) => setSlugInput(
                      e.target.value
                        .toLocaleLowerCase("tr")
                        .replace(/\s+/g, "-")
                        .replace(/[^a-z0-9-]/g, "")
                    )}
                    maxLength={ADMIN_TOPIC_SLUG_MAX_LENGTH}
                  />
                </div>
                <AdminCharCounter value={slugInput} max={ADMIN_TOPIC_SLUG_MAX_LENGTH} />
              </div>
              {/* Channels ("kanal") — ordered multi selection, up to 10;
                  index 0 is the primary channel (list icon) */}
              <div className="form-group">
                <label>{t("topic_channels_label", "Kanallar")}</label>
                <ChannelPicker
                  value={channelsInput}
                  onChange={setChannelsInput}
                  categories={categories}
                  canCreateChannel={isAdmin}
                  onCategoryCreated={handleCategoryCreated}
                  topicCounts={channelTopicCounts}
                />
              </div>
              {/* Active toggle — same pattern as clubs/people/teams edit modals */}
              <div className="form-row">
                <div className="form-group form-group--inline">
                  <label>{t("admin_active")}</label>
                  <button
                    type="button"
                    className={`active-toggle-btn ${activeInput ? "is-active" : "is-inactive"}`}
                    onClick={() => setActiveInput((prev) => !prev)}
                  >
                    {activeInput ? (
                      <>
                        <FaToggleOn /> {t("admin_active")}
                      </>
                    ) : (
                      <>
                        <FaToggleOff /> {t("admin_inactive")}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="modal-cancel-btn" onClick={closeModal}>{t("admin_cancel")}</button>
              <button
                className="modal-confirm-btn"
                onClick={handleEditSave}
                disabled={modalBusy || !titleInput.trim() || !slugInput.trim()}
              >
                <FaCheck /> {t("admin_ct_save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Hard Delete Modal (irreversible — explicit warning) ── */}
      {modalType === "delete" && selectedTopic && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="admin-modal">
            <div className="admin-modal-header ct-danger-header">
              <h3><FaExclamationTriangle /> {t("admin_ct_delete_title")}</h3>
              <button className="modal-close-btn" onClick={closeModal}><FaTimes /></button>
            </div>
            <div className="admin-modal-body">
              {modalError && <div className="modal-error">{modalError}</div>}
              <p className="ct-delete-warning">{t("admin_ct_delete_warning")}</p>
              <div className="ct-delete-facts">
                <div><strong>{t("admin_ct_title_label")}:</strong> {selectedTopic.title}</div>
                <div><strong>Slug:</strong> /gundem/{selectedTopic.slug}</div>
                <div><strong>{t("admin_ct_entry_count")}:</strong> {selectedTopic.entryCount ?? 0}</div>
              </div>
              {/* Entries are removed with the topic — spell that out separately,
                  it is the part that surprises people the most */}
              {(selectedTopic.entryCount ?? 0) > 0 && (
                <p className="ct-delete-warning">
                  {t("admin_ct_delete_entries_warning", { count: selectedTopic.entryCount ?? 0 })}
                </p>
              )}
              <p className="ct-modal-hint">{t("admin_ct_delete_hint")}</p>
            </div>
            <div className="admin-modal-footer">
              <button className="modal-cancel-btn" onClick={closeModal}>{t("admin_cancel")}</button>
              <button className="modal-delete-btn" onClick={handleDelete} disabled={modalBusy}>
                <FaTrash /> {t("admin_permanently_delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lock-with-reason modal — only shown when locking, unlock stays one-click */}
      {lockTarget && (
        <ConfirmModal
          message={t("admin_ct_lock_confirm")}
          confirmLabel={lockBusy ? "..." : t("admin_ct_lock")}
          cancelLabel={t("admin_cancel")}
          onConfirm={handleConfirmLock}
          onCancel={handleCancelLock}
          disabled={lockBusy}
        >
          <LockReasonFields
            reason={lockReasonDraft}
            onReasonChange={setLockReasonDraft}
            visible={lockReasonVisibleDraft}
            onVisibleChange={setLockReasonVisibleDraft}
          />
        </ConfirmModal>
      )}

      {lockInfoTarget && (
        <LockInfoModal
          lockedByUsername={lockInfoTarget.lockedByUsername}
          lockedAt={lockInfoTarget.lockedAt}
          lockReason={lockInfoTarget.lockReason}
          lockReasonVisible={lockInfoTarget.lockReasonVisible}
          onClose={() => setLockInfoTarget(null)}
        />
      )}
    </div>
  );
};